import React from 'react'
import TrueChip from '../TrueChip'
import { FalseIcon } from './Styled'
import { int2HexWithPadAndPrefix, int2HexWithPrefix, round } from '../../Utils/Numbers'
import * as R from 'ramda'
import { datetime } from '../../../Common/Utils/Datetime'
import Config from '../../../Core/Config'
import getStorage from '../../../Common/Services/Storage'
import { toast } from 'react-toastify'
import i18next from 'i18next'
import ReactJson from 'react-json-view'
import { ContentCopy, Delete, Edit } from '@mui/icons-material'
import Download from '@mui/icons-material/Download'

const storageService = getStorage()

export const createColumnsProps = (columns, listDisplay) =>
  columns
    .map((column) => ({
      ...column,
      visible: listDisplay.indexOf(column.id) !== -1,
    }))
    .sort((a, b) => {
      if (listDisplay.indexOf(a.id) !== -1 && listDisplay.indexOf(b.id) !== -1) {
        return listDisplay.indexOf(a.id) - listDisplay.indexOf(b.id)
      } else if (listDisplay.indexOf(a.id) !== -1) {
        return -1
      } else {
        return 1
      }
    })

export const createColumnsPropsWithStorage = (columns, listDisplay, storedColumns) =>
  columns
    .map((column) => ({
      ...column,
      visible: storedColumns
        ? storedColumns?.find(R.propEq('id', column.id))?.visible
        : listDisplay.indexOf(column.id) !== -1,
    }))
    .sort((a, b) => {
      if (storedColumns) {
        return storedColumns.findIndex(R.propEq('id', a.id)) > storedColumns.findIndex(R.propEq('id', b.id)) ? 1 : -1
      } else {
        if (listDisplay.indexOf(a.id) !== -1 && listDisplay.indexOf(b.id) !== -1) {
          return listDisplay.indexOf(a.id) - listDisplay.indexOf(b.id)
        } else if (listDisplay.indexOf(a.id) !== -1) {
          return -1
        } else {
          return 1
        }
      }
    })

export const getDisplayValue = (fieldsMapping, record, field, column) => {
  return fieldsMapping[field] ? fieldsMapping[field](record, 'jsx') : getValue(record, field, column)
}

export const getCsvValue = (fieldsMapping, record, field, column) => {
  return fieldsMapping[field] ? fieldsMapping[field](record, 'csv') : getValue(record, field, column, 'csv')
}

const getNestedValue = (record, field) => field.split('.').reduce((acc, curr) => (acc ? acc[curr] : null), record)

const formatDatetime = (value, format) => datetime(value).format(format || 'Y-MM-DD HH:mm:ss')

const formatDate = (value) => datetime(value).format('Y-MM-DD')

const formatHex = (value, { hexPad }) => (hexPad ? int2HexWithPadAndPrefix(hexPad)(value) : int2HexWithPrefix(value))

const formatBoolean = (value, format) =>
  format === 'jsx' ? value ? <TrueChip /> : <FalseIcon /> : value ? 'true' : 'false'

const formatObject = (value) => (
  <ReactJson
    src={value}
    collapsed={0}
    theme="monokai"
    name="ROOT"
    enableClipboard={(copy) => navigator.clipboard.writeText(JSON.stringify(copy.src, null, '\t'))}
  />
)

const formatStrategies = [
  {
    predicate: R.prop('datetime'),
    handler: (raw, col) => (raw ? formatDatetime(raw, col.datetimeFormat) : null),
  },
  {
    predicate: R.prop('date'),
    handler: (raw) => (raw ? formatDate(raw) : null),
  },
  {
    predicate: R.prop('hex'),
    handler: (raw, col, format) => {
      if (format === 'jsx' || col.exportHex) {
        return R.isNil(raw) ? null : formatHex(raw, { hexPad: col.hexPad })
      }
      return raw
    },
  },
  {
    predicate: R.prop('boolean'),
    handler: (raw, _, format) => formatBoolean(raw, format),
  },
  {
    predicate: R.both(R.prop('decimal'), R.prop('round')),
    handler: (raw, col) => round(raw, col.round),
  },
  {
    // Fallback for Objects
    predicate: (_, raw) => R.is(Object, raw),
    handler: (raw) => formatObject(raw),
  },
]

export const getValue = (record, field, column = null, format = 'jsx') => {
  const raw = getNestedValue(record, field)
  if (!column) return raw

  // Find the first matching strategy
  const strategy = formatStrategies.find((s) => s.predicate(column, raw))

  // Execute logic or return raw if no strategy matched
  return strategy ? strategy.handler(raw, column, format) : raw
}

export const checkStorageInvalidation = (storageKey, id) => {
  if (Config.localStorageInvalidation[storageKey] && storageService.get(storageKey)) {
    if (typeof Config.localStorageInvalidation[storageKey] === 'function') {
      const res = Config.localStorageInvalidation[storageKey](storageKey)
      if (res === 'REMOVE') {
        storageService.remove(`${id}_CHANGELIST_TABLE_SETTINGS`)
        toast.warn(i18next.t('common:ui.ForceResetChangelistTableSettingsWarning'))
      }
    } else {
      const storageTimestamp = storageService.getNested(storageKey, 'timestamp', 0)
      if (storageTimestamp < Config.localStorageInvalidation[storageKey]) {
        storageService.remove(`${id}_CHANGELIST_TABLE_SETTINGS`)
        toast.warn(i18next.t('common:ui.ForceResetChangelistTableSettingsWarning'))
      }
    }
  }
}

export const editAction = (permFunc, condFunc) => {
  const obj = {
    id: 'EDIT',
    label: i18next.t('common:generic.Edit'),
    icon: <Edit />,
  }
  if (permFunc) {
    obj.perm = permFunc
  }
  if (condFunc) {
    obj.cond = condFunc
  }
  return obj
}

export const deleteAction = (permFunc, showIcon = true, id = 'DELETE', condFunc) => {
  const obj = {
    id,
    label: i18next.t('common:generic.Delete'),
    icon: showIcon ? <Delete /> : null,
  }
  if (permFunc) {
    obj.perm = permFunc
  }
  if (condFunc) {
    obj.cond = condFunc
  }
  return obj
}

export const cloneAction = (permFunc) => {
  const obj = {
    id: 'CLONE',
    label: i18next.t('common:generic.Clone'),
    icon: <ContentCopy />,
  }
  if (permFunc) {
    obj.perm = permFunc
  }
  return obj
}

export const downloadAction = (permFunc, showIcon = true) => {
  const obj = {
    id: 'DOWNLOAD',
    label: i18next.t('common:generic.Download'),
    icon: showIcon ? <Download /> : null,
  }
  if (permFunc) {
    obj.perm = permFunc
  }
  return obj
}
